import { Form, Input, Modal, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { QueryErrorAlert } from "design-system/components/QueryErrorAlert";
import { useCustomNotification } from "design-system/hooks";
import { useSandboxAuth } from "sandbox/contexts/SandboxAuthContext";

import s from "./UpdatePasswordModal.module.css";

interface FormValues {
  confirmPassword: string;
  currentPassword: string;
  newPassword: string;
}

interface UpdatePasswordModalProps {
  onClose: () => void;
  open: boolean;
}

export const UpdatePasswordModal = ({
  open,
  onClose,
}: UpdatePasswordModalProps) => {
  const { t } = useTranslation(["sandbox", "common"]);
  const { updatePassword } = useSandboxAuth();
  const { showNotification } = useCustomNotification();
  const [form] = Form.useForm<FormValues>();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const rules = [
    {
      required: true,
      message: t("sandbox:validation.required", {
        field: t("sandbox:labels.password"),
      }),
    },
    {
      min: 8,
      message: t("sandbox:validation.minChars", { count: 8 }),
    },
    {
      pattern: /\d/,
      message: t("sandbox:validation.mustIncludeNumber"),
    },
    {
      pattern: /[A-Z]/,
      message: t("sandbox:validation.mustIncludeUppercase"),
    },
    {
      pattern: /[a-z]/,
      message: t("sandbox:validation.mustIncludeLowercase"),
    },
  ];

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setError("");
      await updatePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      showNotification({
        type: "success",
        message: t("common:status.success"),
        description: t(
          "sandbox:components.updatePasswordModal.savePasswordSuccess",
        ),
      });
      form.resetFields();
      onClose();
    } catch (err) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      setError(
        (err as { message?: string })?.message ??
          "An unexpected error occurred",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t("sandbox:components.updatePasswordModal.title")}
      okText={t("sandbox:actions.saveNewPassword")}
      cancelText={t("common:components.buttons.cancel")}
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        setError("");
        onClose();
      }}
      confirmLoading={submitting}
      width={460}
    >
      <Typography.Text className={s.subtitle}>
        {t("sandbox:components.updatePasswordModal.subtitle")}
      </Typography.Text>

      {error && (
        <div className={s.errorAlert}>
          <QueryErrorAlert description={error} />
        </div>
      )}

      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        autoComplete="off"
      >
        <Form.Item
          name="currentPassword"
          label={t("sandbox:labels.currentPassword")}
          rules={rules}
        >
          <Input.Password placeholder={t("sandbox:placeholders.password")} />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label={t("sandbox:labels.newPassword")}
          tooltip={t("sandbox:validation.passwordExtra")}
          rules={rules}
        >
          <Input.Password placeholder={t("sandbox:placeholders.password")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
